import { useState, useMemo } from 'react';
import { message } from 'antd';
import { usePharmacies } from './usePharmacies';
import { nameToAbbr } from './useStates';

const resolveStateAbbr = (state) => {
    if (!state) return null;
    return state.length === 2 ? state.toUpperCase() : (nameToAbbr(state) ?? null);
};

const toPharmacyEntry = (p, fallbackId) => ({
    pharmacyId: p?.pharmacyId || fallbackId,
    name: p?.name || fallbackId,
    state: p?.state ?? null,
    stateAbbr: resolveStateAbbr(p?.state),
});

/**
 * Manages the search-and-multi-select interaction for pharmacies.
 *
 * @param {object}   [opts]
 * @param {function} [opts.onFirstSelect] - Called with the pharmacy object when the
 *                                          first pharmacy is added to an empty selection.
 *                                          Useful for auto-populating related form fields.
 */
export const usePharmacyMultiSelect = ({ onFirstSelect } = {}) => {
    const [selectedPharmacies, setSelectedPharmacies] = useState([]);
    const [loadingPharmacies, setLoadingPharmacies] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    const {
        pharmacySearchResults,
        pharmaciesLoading,
        fetchPharmacy,
        searchPharmacies,
    } = usePharmacies();

    const pharmacyOptions = useMemo(
        () => pharmacySearchResults?.map(({ pharmacyId, name, city, state }) => ({
            label: `${name}${city ? `, ${city}` : ''}${state ? ` ${state}` : ''} (${pharmacyId})`,
            value: pharmacyId,
        })),
        [pharmacySearchResults]
    );

    const handleSearch = (val) => {
        setSearchValue(val);
        searchPharmacies(val);
    };

    const handleSelectPharmacy = (pharmacyId) => {
        const match = pharmacySearchResults.find(p => p.pharmacyId === pharmacyId);
        if (!match) return;

        if (selectedPharmacies.some(p => p.pharmacyId === pharmacyId)) {
            message.warning(`${match.name} is already added`);
            return;
        }

        const isFirst = selectedPharmacies.length === 0;
        setSelectedPharmacies(prev => [...prev, toPharmacyEntry(match, pharmacyId)]);

        if (isFirst) onFirstSelect?.(match);

        setSearchValue('');
    };

    const handleRemovePharmacy = (pharmacyId) => {
        setSelectedPharmacies(prev => prev.filter(p => p.pharmacyId !== pharmacyId));
    };

    /**
     * Resets selection and search state. Call when opening the modal in create mode.
     */
    const reset = () => {
        setSelectedPharmacies([]);
        setSearchValue('');
    };

    /**
     * Hydrates selected pharmacies from a list of IDs by fetching their details.
     * Call when opening the modal in edit mode.
     *
     * @param {string[]} ids
     */
    const hydrate = (ids) => {
        if (!ids?.length) {
            setSelectedPharmacies([]);
            return;
        }

        setLoadingPharmacies(true);
        Promise.all(
            ids.map(id =>
                fetchPharmacy(id)
                    .then(p => toPharmacyEntry(p, id))
                    .catch(() => toPharmacyEntry(null, id))
            )
        )
            .then(pharmacies => setSelectedPharmacies(pharmacies))
            .catch(() => message.error('Failed to load pharmacy details'))
            .finally(() => setLoadingPharmacies(false));
    };

    return {
        selectedPharmacies,
        loadingPharmacies,
        searchValue,
        setSearchValue,
        pharmacyOptions,
        pharmaciesLoading,
        handleSearch,
        handleSelectPharmacy,
        handleRemovePharmacy,
        reset,
        hydrate,
    };
};
